/**
 * Converts stored HTML (Quill notes, rich-text messages) to plain text without
 * parsing it as DOM. Never assign untrusted HTML to `innerHTML` to extract text:
 * even on a detached element, `<img onerror>` handlers run (SEC-013).
 */

export interface HtmlToPlainTextOptions {
	/**
	 * Keep paragraph and `<br>` boundaries as newlines (for `white-space: pre-wrap`
	 * views). Default `false` collapses everything to a single line for previews.
	 */
	preserveLineBreaks?: boolean;
}

const BLOCK_TAGS = 'p|div|li|h[1-6]|tr|blockquote|section|article';
const BLOCK_CLOSE_RE = new RegExp(`</(${BLOCK_TAGS})>`, 'gi');
const BLOCK_OPEN_RE = new RegExp(`<(${BLOCK_TAGS})[^>]*>`, 'gi');

const decodeEntities = (text: string): string =>
	text
		.replace(/&nbsp;/gi, ' ')
		.replace(/&lt;/gi, '<')
		.replace(/&gt;/gi, '>')
		.replace(/&quot;/gi, '"')
		.replace(/&#39;|&apos;/gi, "'")
		.replace(/&#(\d+);/g, (_, code) => String.fromCodePoint(Number(code)))
		.replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCodePoint(parseInt(hex, 16)))
		// Last, so "&amp;lt;" decodes to the literal text "&lt;" rather than "<".
		.replace(/&amp;/gi, '&');

export function htmlToPlainText(
	html: string | undefined | null,
	{ preserveLineBreaks = false }: HtmlToPlainTextOptions = {}
): string {
	if (!html) return '';

	const separator = preserveLineBreaks ? '\n' : ' ';
	const withoutTags = String(html)
		.replace(/<br\s*\/?>/gi, separator)
		.replace(BLOCK_CLOSE_RE, separator)
		// Line mode: one newline per closed block (Quill emits one <p> per line).
		.replace(BLOCK_OPEN_RE, preserveLineBreaks ? '' : ' ')
		.replace(/<[^>]*>/g, '');

	const text = decodeEntities(withoutTags);

	if (!preserveLineBreaks) {
		return text.replace(/\s+/g, ' ').trim();
	}

	return text
		.split('\n')
		.map((line) => line.replace(/[^\S\n]+/g, ' ').trim())
		.join('\n')
		.replace(/\n{3,}/g, '\n\n')
		.trim();
}
